import {
  Badge,
  CopyToClipboardButton,
  FlexBox,
  Icon,
  IconButton,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { type MouseEvent, useEffect } from 'react';
import { MarkRequired } from 'ts-essentials';

import { companiesApi } from 'apis/companies';
import { appActions } from 'app/app.actions';
import { twilioActions } from 'app/middleware/twilio/actions/twilioActions';
import { numberFormatterInternational } from 'app/utils/telephonyHelpers';
import { AppLink } from 'components/appLink';
import { AddContactAttributesModal } from 'components/contact';
import { CustomFieldChip } from 'components/customFields';
import { PartialChildren } from 'components/partialChildren';
// eslint-disable-next-line no-restricted-imports
import { composeEmailActions } from 'components/shared/composeEmail/actions/composeEmailActions';
import { useCustomFields } from 'utils/customFields';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { getUserDisplayName, userTypeAssets } from 'utils/user';

import { ContactItemsProps } from '../../../contactInfo/components/contactItems/contactItems';
import { ContactInfo } from '../../../contactInfo/contactInfo';

import { ContactCallAction } from './components/callAction/contactCallAction';
import { ContactEmailAction } from './components/emailAction/contactEmailAction';
import { MoreDropdownAction } from './components/moreDropdownAction/moreDropdownAction';
import styles from './userInfo.module.scss';

interface UserInfoCardProps {
  user: User;
}

export function UserInfo({ user }: UserInfoCardProps): JSX.Element {
  const hasCustomAttributeAccess = useHasAccess('contact.custom_attribute');
  const hasReplyAccess = useHasAccess('conversation.reply');
  const dispatch = useAppDispatch();
  const { company_id, external_id } = user;

  const { telephonyFromNumber } = useAppSelector((state) => ({
    telephonyFromNumber: state.telephony.selectedFromNumber,
  }));

  const { customFields } = useCustomFields({
    entityId: 'contact',
    entityInstanceId: user.id,
  });

  const [getCompany, { data: companyResponse }] =
    companiesApi.useLazyReadCompanyQuery();

  useEffect(() => {
    if (company_id) {
      getCompany({ id: company_id });
    }
  }, [company_id, getCompany]);

  const relatedCompanyLink = company_id ? (
    <AppLink
      path="/contacts/company/:id"
      params={{ path: { id: company_id } }}
      onClick={() => ampli.contactsCardViewCompanyClicked()}
    >
      {companyResponse?.data.name}
    </AppLink>
  ) : undefined;

  const secondaryLabel = [
    relatedCompanyLink,
    userTypeAssets[user.type]?.prettyName,
  ]
    .filter(Boolean)
    .map((label, index) => (
      <span key={index} className={styles.secondaryLabelItem}>
        {label === relatedCompanyLink ? <Icon name="building" size={16} /> : ''}
        <span>{label}</span>
      </span>
    ));

  /** Gets formatted phone number or null in case the user doesn't have a phone. */
  const getPhoneNumberFormat = (userPhoneNumber: string | null): string => {
    const phoneNumber = userPhoneNumber?.trim();

    if (!phoneNumber) return '';

    return phoneNumber.startsWith('+')
      ? numberFormatterInternational({ phoneNumber })
      : phoneNumber;
  };

  const getCopyToClipboardElement = (textToCopy: string) => (
    <Tooltip content="Copy">
      <CopyToClipboardButton
        textToCopy={textToCopy}
        tooltip={false}
        clickedProps={{ icon: 'done' }}
        buttonProps={{ variant: 'tertiary', icon: 'copy' }}
      />
    </Tooltip>
  );

  const handleEmailClick = (
    e: MouseEvent<HTMLAnchorElement>,
    email = user.email,
  ): void => {
    e.preventDefault();

    if (!email) return;

    dispatch(
      composeEmailActions.composeEmailOpenModal({
        initialValues: { toUser: [{ user: { ...user, email } }] },
      }),
    );
  };

  const handlePhoneNumberClick = (
    e: MouseEvent<HTMLAnchorElement>,
    phoneNumber = user.phone_number,
  ): void => {
    e.preventDefault();

    if (!user.email) return;

    dispatch(
      twilioActions.twilioMakeCall({
        userId: user.id,
        toNumber: phoneNumber,
        fromNumber: telephonyFromNumber?.e164_format as string,
      }),
    );
  };

  const contactItems: ContactItemsProps = {
    items: [],
    limit: 5,
  };
  const customFieldItems: ContactItemsProps = {
    items: [],
    limit: 3,
  };

  const emailTooltip = hasReplyAccess ? 'Compose email' : '';

  if (user.email) {
    contactItems.items.push({
      icon: 'mail',
      label: 'Email',
      labelMeta: user.additional_emails?.length ? (
        <Badge variant="primary" size="small">
          Primary
        </Badge>
      ) : null,
      value: hasReplyAccess ? (
        <a href={`mailto:${user.email}`} onClick={handleEmailClick}>
          {user.email}
        </a>
      ) : (
        user.email
      ),
      valueTooltip: emailTooltip,
      actions: getCopyToClipboardElement(user.email),
    });
  }

  if (user.additional_emails) {
    user.additional_emails.forEach((additionalEmail) => {
      contactItems.items.push({
        icon: 'mail',
        label: 'Email',
        value: hasReplyAccess ? (
          <a
            href={`mailto:${additionalEmail}`}
            onClick={(e) => handleEmailClick(e, additionalEmail)}
          >
            {additionalEmail}
          </a>
        ) : (
          additionalEmail
        ),
        valueTooltip: emailTooltip,
        actions: getCopyToClipboardElement(additionalEmail),
      });
    });
  }

  const phoneTooltip = hasReplyAccess ? 'Call' : '';

  if (user.phone_number) {
    contactItems.items.push({
      icon: 'phone',
      label: 'Phone',
      labelMeta: user.additional_phone_numbers?.length ? (
        <Badge variant="primary" size="small">
          Primary
        </Badge>
      ) : null,
      value: hasReplyAccess ? (
        <a href={`tel:${user.phone_number}`} onClick={handlePhoneNumberClick}>
          {getPhoneNumberFormat(user.phone_number)}
        </a>
      ) : (
        getPhoneNumberFormat(user.phone_number)
      ),
      valueTooltip: phoneTooltip,
      actions: getCopyToClipboardElement(user.phone_number),
    });
  }

  if (user.additional_phone_numbers) {
    user.additional_phone_numbers.forEach((additionalPhoneNumber) => {
      contactItems.items.push({
        icon: 'phone',
        label: 'Phone',
        value: hasReplyAccess ? (
          <a
            href={`tel:${additionalPhoneNumber}`}
            onClick={(e) => handlePhoneNumberClick(e, additionalPhoneNumber)}
          >
            {getPhoneNumberFormat(additionalPhoneNumber)}
          </a>
        ) : (
          getPhoneNumberFormat(additionalPhoneNumber)
        ),
        valueTooltip: phoneTooltip,
        actions: getCopyToClipboardElement(additionalPhoneNumber),
      });
    });
  }

  if (external_id) {
    contactItems.items.push({
      icon: 'id-card',
      label: 'External ID',
      value: external_id,
      actions: getCopyToClipboardElement(external_id),
    });
  }

  if (customFields.length) {
    const customFieldsWithValue = customFields.filter(
      (customField) => customField.value !== undefined,
    ) as readonly MarkRequired<(typeof customFields)[number], 'value'>[];

    if (customFieldsWithValue.length) {
      customFieldItems.items.push({
        icon: 'input',
        label: 'Contact attributes',
        value: (
          <FlexBox alignItems="center" gap="200" flexWrap="wrap">
            <PartialChildren limit={customFieldItems.limit}>
              {customFieldsWithValue.map((customField) => (
                <CustomFieldChip
                  key={customField.attribute.id}
                  customField={customField}
                />
              ))}
            </PartialChildren>
          </FlexBox>
        ),
        actions: hasCustomAttributeAccess ? (
          <IconButton
            icon="pen"
            variant="tertiary"
            onClick={() => {
              dispatch(
                appActions.showModal({
                  modal: {
                    type: 'contact:addContactAttributes',
                    arg: { user },
                  },
                }),
              );
            }}
          />
        ) : null,
        flexBoxProps: {
          flexDirection: 'column',
        },
      });
    }
  }

  const contactItemSections = [contactItems, customFieldItems].filter(
    ({ items }) => items.length,
  );

  const actions = (
    <FlexBox gap="200" alignItems="center">
      {hasReplyAccess && user.phone_number && <ContactCallAction user={user} />}
      {hasReplyAccess && user.email && <ContactEmailAction user={user} />}
      <MoreDropdownAction user={user} />
    </FlexBox>
  );

  return (
    <>
      <ContactInfo
        avatar={{
          id: user.id,
          avatar_url: user.avatar_url,
          name: user.name,
          phone_number: user.phone_number,
        }}
        primaryLabel={getUserDisplayName(user)}
        secondaryLabel={secondaryLabel}
        contactItemSections={contactItemSections}
        actions={actions}
      />

      <AddContactAttributesModal />
    </>
  );
}
